import React, {
    createContext,
    useReducer,
    useEffect,
    useCallback,
    ReactNode,
    useContext,
    useState
  } from "react";
  import { conversationAPI } from "./api";
  import { useSettingsContext } from "./SettingsProvider";
  import { useAgent } from './AgentsContext';
  import { useAuthContext } from './AuthProvider';
import settings from "./settings";
  const { REACT_APP_BACKEND_URL } = settings;


  export type PromptType = string;
  
  interface BaseMessage {
    content: string;
  }
  
  interface QuestionMessage extends BaseMessage {
    type: "question";
  }
  
  interface SourceLink {
    source_path: string;
    document_uuid: string;
    }

    interface AnswerMessage extends BaseMessage {
        type: "answer";
        sources: SourceLink[];
    }
  type Message = QuestionMessage | AnswerMessage;
  
  interface Chat {
    id: string;
    title: string;
    messages: Message[];
    agent_id: string;
    isWaiting: boolean;
  }
  
  interface ChatHistoryState {
    selectedChatId: string | null;
    chatHistory: Chat[];
  }
  
  interface LoadChatHistoryAction {
    type: "LOAD_CHAT_HISTORY";
    payload: Chat[];
  }
  
  interface AddChatAction {
    type: "ADD_CHAT";
    payload: Chat;
  }
  
  interface AddMessageAction {
    type: "ADD_MESSAGE";
    payload: {
      chatId: string;
      message: Message;
    };
  }
  interface UpdateMessageAction {
    type: "UPDATE_MESSAGE";
    payload: {
      chatId: string | null;
      message: Message;
    };
  }
  
  interface NewChatAction {
    type: "NEW_CHAT";
    payload: {};
  }
  
  interface ToggleWaitingForAction {
    type: "TOGGLE_WAITING_FOR_RESPONSE";
    payload: { chatId: string };
  }
  
  interface ChangeChatAction {
    type: "CHANGE_SELECTED_CHAT";
    payload: {
      chatId: string;
      chat: Chat;
    };
  }

	interface DeleteChatAction {
		type: "DELETE_CHAT";
		payload: { chatId: string };
	}
	
  
  type Action =
    | LoadChatHistoryAction
    | AddChatAction
    | AddMessageAction
    | NewChatAction
    | ChangeChatAction
    | ToggleWaitingForAction
    | UpdateMessageAction
		| DeleteChatAction;
  
  interface ChatHistoryContextProps {
    chatHistory: Chat[];
    selectedChat?: Chat;
    newChat: () => void;
    sendMessage: (
      question: string,
      patientName: string | null, 
      coachName: string | null, 
      date: string | null
    ) => void;
    changeSelectedChat: (chatId: string) => void;
		deleteChat: (chatId: string) => void;
    loading: boolean;
  }
  
  // Create a context for the chat history
  export const ChatHistoryContext = createContext<
    ChatHistoryContextProps | undefined
  >(undefined);
  
  export const useChatHistory = () => {
    const context = useContext(ChatHistoryContext);
    if (!context) {
      throw new Error("useChatHistory must be used within a ChatHistoryProvider");
    }
    return context;
  };
  
  const initialState: ChatHistoryState = {
    selectedChatId: null,
    chatHistory: [],
  };
  
  // Reducer function to update chat history
  const chatHistoryReducer = (
    state: ChatHistoryState,
    { type, payload }: Action
  ): ChatHistoryState => {
    switch (type) {
      case "LOAD_CHAT_HISTORY":
        return { ...state, chatHistory: payload };
      case "ADD_CHAT":
        return {
          ...state,
          chatHistory: [payload, ...state.chatHistory],
          selectedChatId: payload.id,
        };
      case "ADD_MESSAGE":
        const chat = state.chatHistory.find((chat) => chat.id === payload.chatId);
        if (!chat) {
          return state;
        }
        const newChat = {
          ...chat,
          messages: [...chat.messages, payload.message],
        };
        return {
          ...state,
          chatHistory: state.chatHistory.map((chat) =>
            chat.id === newChat.id ? newChat : chat
          ),
        };
      case "UPDATE_MESSAGE":
        const uchat = state.chatHistory.find(
          (chat) => chat.id === payload.chatId
        );
        if (!uchat) {
          return state;
        }
        const messages = uchat.messages;
        const oldMessage = messages[messages.length - 1];
        const restOfMessages = messages.slice(0, messages.length - 1);
        //append the payload to the old message
        if (oldMessage.type !== "answer") {
          return state;
        }
        if (payload.message.type !== "answer") {
          return state;
        }
  
        const message: AnswerMessage = {
          ...oldMessage,
          content: payload.message.content,
          sources: payload.message.sources,
        };
        const newUChat = {
          ...uchat,
          messages: [...restOfMessages, message],
        };
        return {
          ...state,
          chatHistory: state.chatHistory.map((chat) =>
            chat.id === newUChat.id ? newUChat : chat
          ),
        };
      case "NEW_CHAT":
        return { ...state, selectedChatId: null };
      case "CHANGE_SELECTED_CHAT":
        const updatedChatHistory = state.chatHistory.map((chat) =>
          chat.id === payload.chat.id ? payload.chat : chat
        );
        return { ...state, selectedChatId: payload.chat.id, chatHistory: updatedChatHistory };
      case "TOGGLE_WAITING_FOR_RESPONSE":
        const chat2 = state.chatHistory.find(
          (chat) => chat.id === payload.chatId
        );
        if (!chat2) {
          return state;
        }
        const newChat2 = {
          ...chat2,
          isWaiting: !chat2.isWaiting,
        };
        return {
          ...state,
          chatHistory: state.chatHistory.map((chat2) =>
            chat2.id === newChat2.id ? newChat2 : chat2
          ),
        };
			case "DELETE_CHAT":
				return {
					...state,
					chatHistory: state.chatHistory.filter(chat => chat.id !== payload.chatId),
				};
		
      default:
        return state;
    }
  };
  
  
  const loadChatHistory = async (loginWithMicrosoft: () => void): Promise<Chat[]> => {
    try {
        const token = localStorage.getItem('access_token');
        
        if (!token) {
            console.warn('No access token found, redirecting to login.');
            loginWithMicrosoft();  
            return [];
        }

      const response = await fetch(`${REACT_APP_BACKEND_URL}/chat/sessions`, {
        method: "GET",
        credentials: "include",
        headers: {
            "Authorization": `Bearer ${token}`,  
        },
      });
      console.log(response)
      if (!response.ok) {
        if (response.status == 401){
            loginWithMicrosoft();
        }
        throw new Error('Network response was not ok');
      }
      const chatList = await response.json();
      console.log('/chat/history response', chatList);
      return chatList;
    } catch (error) {
      console.error('Failed to load chat history:', error);
      return [];
    }
  };
  
  
  const fetchChatById = async (chatId: string, loginWithMicrosoft: () => void): Promise<Chat | null> => {
    try {
        const token = localStorage.getItem('access_token');
        
        if (!token) {
            console.warn('No access token found, redirecting to login.');
            loginWithMicrosoft(); 
        }

      const response = await fetch(`${REACT_APP_BACKEND_URL}/chat/history/${chatId}`, {
        method: "GET",
        credentials: "include",
        headers: {
            "Authorization": `Bearer ${token}`,  
        },
      });
      if (!response.ok) {
        if (response.status == 401){
            loginWithMicrosoft()
        }
        throw new Error('Network response was not ok');
      }
      const chat = await response.json();
      return chat;
    } catch (error) {
      console.error(`Failed to load chat with id ${chatId}:`, error);
      return null;
    }
  };
  
  
  interface ChatHistoryProviderProps {
    children: ReactNode;
  }
  
  export const ChatHistoryProvider: React.FC<ChatHistoryProviderProps> = ({ children }) => {
    const { loginWithMicrosoft } = useAuthContext();
    const settings = useSettingsContext();
    const { agents, setSelectedAgent, selectedAgent } = useAgent(); 
    const [state, dispatch] = useReducer(chatHistoryReducer, initialState);
    const [loading, setLoading] = useState<boolean>(true);  

    useEffect(() => {
      const initializeChatHistory = async () => {
        setLoading(true);  
        const initialChatHistory = await loadChatHistory(loginWithMicrosoft);
        dispatch({ type: "LOAD_CHAT_HISTORY", payload: initialChatHistory });
        setLoading(false);  
      };
      initializeChatHistory();
    }, []);
  
    const sendMessage = useCallback(
        async (question: string, 
            patientName: string | null, 
            coachName: string | null, 
            date: string | null) => {
        const token = localStorage.getItem('access_token');
        
        if (!token) {
            console.warn('No access token found, redirecting to login.');
            loginWithMicrosoft(); 
            return [];
        }

          let chatId = state.selectedChatId;
          if (!chatId) {
            const selectedAgentName = agents.find(agent => agent.id === selectedAgent)?.name || "No agent selected";
      
            const response = await fetch(`${REACT_APP_BACKEND_URL}/chat/new`, {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',                
                "Authorization": `Bearer ${token}`, 

              },
              body: JSON.stringify({ query: question, agent_id: selectedAgent })
            });
      
            if (!response.ok) {
                if (response.status == 401){
                    loginWithMicrosoft();
                    return;
                }
              throw new Error('Failed to fetch new chat ID');
            }
      
            const data = await response.json();
            chatId = data.id;
            if (!chatId) return;
            const agentId = data.agent_id;
            setSelectedAgent(agentId)
            dispatch({
              type: "ADD_CHAT",
              payload: {
                id: chatId,
                messages: [
                  {
                    type: "question",
                    content: question,
                  },
                ],
                agent_id: agentId,
                isWaiting: false,
                title: question,
              },
            });
          } else {
            dispatch({
              type: "ADD_MESSAGE",
              payload: {
                chatId,
                message: { type: "question", content: question },
              },
            });
          }
          dispatch({ type: "TOGGLE_WAITING_FOR_RESPONSE", payload: { chatId } });
          
          const response = await conversationAPI({
            chatId,
            question,
            agentId: selectedAgent,
            patientName,
            coachName,
            date,
          }, loginWithMicrosoft);
          
          if (!response.ok) {
            if (response.status === 401) {
              loginWithMicrosoft();
            }
            throw new Error("Failed to send message");
          }
          
          const reader = response?.body?.getReader();
          const decoder = new TextDecoder();
          let buffer: string = ""; // Buffer for accumulating partial chunks
          let accumulatedContent = ""; // Tracks the full rendered content
          let isCursorVisible = true; // Tracks whether the cursor is visible
          let typingComplete = false; // Flag to track typing completion

          // Initialize message object
          let message: Message = {
            type: "answer",
            content: "",
            sources: [],
          };
          
          // Dispatch initial state
          dispatch({ type: "ADD_MESSAGE", payload: { chatId, message } });
          
          // Blink cursor
          const startCursorBlinking = () => {
            return setInterval(() => {
              if (!typingComplete) {
                isCursorVisible = !isCursorVisible; 
                const cursor = isCursorVisible ? "█" : ""; 
                message.content = accumulatedContent + cursor;
                dispatch({ type: "UPDATE_MESSAGE", payload: { chatId, message } });
              }
            }, 500); 
          };

          const cursorInterval = startCursorBlinking();

          const addChunkWithDelay = async (chunk: string) => {
            return new Promise<void>((resolve) => {
              setTimeout(() => {
                accumulatedContent += chunk; 
                const cursor = isCursorVisible ? "█" : ""; 
                message.content = accumulatedContent + cursor;

                dispatch({ type: "UPDATE_MESSAGE", payload: { chatId, message } });

                resolve();
              }, 10); // delay (10ms)
            });
          };
          
          if (reader) {
            while (true) {
              const { done, value } = await reader.read();
              if (done) break;
          
              buffer += decoder.decode(value, { stream: true });
          
              const lines = buffer.split("\n");
              buffer = lines.pop() || ""; 
          
              for (const line of lines) {
                if (line.startsWith("data:")) {
                  const jsonString = line.replace(/^data:\s*/, "");
                  try {

                    const parsedResponse = JSON.parse(jsonString);              
                    const contentToAdd = parsedResponse.content || "";
                    await addChunkWithDelay(contentToAdd); 
                    message.sources = parsedResponse.sources || [];

                  } catch (e) {
                    console.error("Failed to parse SSE message", e);
                  }
                }
              }
            }
          }
          // Stop cursor blinking when typing is complete
          typingComplete = true;
          clearInterval(cursorInterval);
          message.content = accumulatedContent; // Remove the cursor after completion
          dispatch({ type: "TOGGLE_WAITING_FOR_RESPONSE", payload: { chatId } });
          dispatch({ type: "UPDATE_MESSAGE", payload: { chatId, message } });

        },
        [state.chatHistory, state.selectedChatId, settings, agents, selectedAgent, setSelectedAgent]
      );
  
    const newChat = () => dispatch({ type: "NEW_CHAT", payload: {} });
  
    const changeSelectedChat = useCallback(
      async (chatId: string) => {
        const chat = await fetchChatById(chatId, loginWithMicrosoft);
        if (chat) {
          dispatch({ type: "CHANGE_SELECTED_CHAT", payload: { chatId, chat } });
          setSelectedAgent(chat.agent_id)
        }
      },
      []
    );

		const deleteChat = useCallback((chatId: string) => {
			dispatch({ type: "DELETE_CHAT", payload: { chatId } });
		}, []);
		
  
    const context: ChatHistoryContextProps = {
      chatHistory: state.chatHistory,
      selectedChat: state.chatHistory.find(
        (chat) => chat.id === state.selectedChatId
      ),
      sendMessage,
      newChat,
      changeSelectedChat,
			deleteChat,
      loading
    };
  
    return (
      <ChatHistoryContext.Provider value={context}>
        {children}
      </ChatHistoryContext.Provider>
    );
  };
  